/**
 * VouchersTab — CON-03: Comprobantes Contables
 */
import { todayISO } from '../../../lib/utils'
import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DatePicker from '@/components/DatePicker'
import { useTenantLocale } from '@/lib/locale'
import { fmtDate } from '@/lib/utils'
import { Plus, Loader2, CheckCircle, RotateCcw, Trash2, Pencil, FileText, XCircle, Zap, X, ExternalLink, AlertTriangle } from 'lucide-react'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { Modal } from '@/components/ds/Modal'
import { KebabMenu } from '@/components/KebabMenu'
import { usePermission } from '@/hooks/usePermission'
import {
  useVouchers, useVoucher,
  useApproveVoucher, useReverseVoucher, useDeleteVoucher, useRejectVoucher,
  useBulkApproveVouchers, useBulkRejectVouchers,
  type AccountingVoucher, type AccountingVoucherListItem, type VoucherType,
  type VoucherLine,
} from '../hooks/useAccounting'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { VoucherForm, AttachmentsSection, TYPE_LABELS, fmtCLP } from './VoucherForm'
import apiClient from '@/lib/apiClient'
import { PageSizeSelector, DEFAULT_PAGE_SIZE } from '@/components/PageSizeSelector'
import { StatusBadge } from '../../../components/ds/StatusBadge'
import { VOUCHER_STATUS } from '../../../components/ds/statusCatalogs'
import { Pagination } from '@/components/ds/Pagination'

const SOURCE_TYPE_CONFIG: Record<string, { label: string; route: string }> = {
  sale:      { label: 'Documento de Venta', route: '/sales/documents' },
  purchase:  { label: 'Factura Proveedor',  route: '/purchases/invoices' },
  factoring: { label: 'Op. Factoring',      route: '/treasury/factoring' },
}

function SourceLink({ sourceType, sourceId, onClick }: { sourceType: string; sourceId: string; onClick: () => void }) {
  const cfg = SOURCE_TYPE_CONFIG[sourceType]
  if (!cfg) return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-info-surface text-info-content-soft rounded border border-info-border">
      <Zap size={10} />Auto — {sourceType}
    </span>
  )
  return (
    <button onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-info-surface text-info-content rounded border border-info-border hover:bg-info-surface-strong hover:border-violet-300 dark:border-violet-700 transition-colors">
      <Zap size={10} />{cfg.label}
      <ExternalLink size={10} />
    </button>
  )
}


// ─── VoucherDetailModal ────────────────────────────────────────────────────────

interface DetailModalProps {
  voucherId: string
  onClose: () => void
  onEdit: (v: AccountingVoucher) => void
  onEditApproved: (v: AccountingVoucher) => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onReverse: (id: string) => void
  onDelete: (id: string) => void
  onNavigateSource?: (sourceType: string, sourceId: string) => void
}

function VoucherDetailModal({ voucherId, onClose, onEdit, onEditApproved, onApprove, onReject, onReverse, onDelete, onNavigateSource }: DetailModalProps) {
  const locale = useTenantLocale()
  const { can } = usePermission()
  const canEditApproved = can('accounting.vouchers', 'edit_approved')
  const { data: voucher, isLoading } = useVoucher(voucherId)
  const [pdfLoading, setPdfLoading] = useState(false)


  const handleDownloadPdf = async () => {
    if (!voucher || pdfLoading) return
    setPdfLoading(true)
    try {
      const res = await apiClient.get(`/accounting/vouchers/${voucher.id}/pdf/`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      window.open(url, '_blank')
    } catch (exc) {
      // eslint-disable-next-line no-console
      console.error('Error descargando PDF de comprobante', exc)
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span>
          Detalle del Comprobante
          {voucher?.number && <span className="ml-2 text-content-subtle font-mono text-sm">#{voucher.number}</span>}
        </span>
      }
      actions={
        <>
          {voucher && (
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
              <div className="flex gap-2">
                {voucher.status === 'draft' && (
                  <>
                    <button onClick={() => { onClose(); onEdit(voucher) }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
                      <Pencil size={13} />Editar
                    </button>
                    <button onClick={() => { onApprove(voucher.id); onClose() }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-success-content border border-success-border rounded-lg hover:bg-success-surface">
                      <CheckCircle size={13} />Aprobar
                    </button>
                    <button onClick={() => { onReject(voucher.id); onClose() }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30">
                      <XCircle size={13} />Rechazar
                    </button>
                    <button onClick={() => { onDelete(voucher.id); onClose() }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-danger-content-soft border border-danger-border rounded-lg hover:bg-danger-surface">
                      <Trash2 size={13} />Eliminar
                    </button>
                  </>
                )}
                {voucher.status === 'approved' && (
                  <>
                    {canEditApproved && (
                      <button onClick={() => { onClose(); onEditApproved(voucher) }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-600 dark:text-rose-300 border border-rose-200 dark:border-rose-800 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/30">
                        <AlertTriangle size={13} />Editar aprobado
                      </button>
                    )}
                    <button onClick={() => { onReverse(voucher.id); onClose() }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface">
                      <RotateCcw size={13} />Revertir
                    </button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-hover bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  title="Descargar PDF"
                >
                  {pdfLoading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                  Descargar PDF
                </button>
                <button onClick={onClose}
                  className="px-4 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando...
        </div>
      ) : voucher ? (
        <>
          <div className="space-y-4">
            {/* Cabecera */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-content-muted mb-0.5">Tipo</p>
                <p className="font-medium">{TYPE_LABELS[voucher.voucher_type]}</p>
              </div>
              <div>
                <p className="text-xs text-content-muted mb-0.5">Fecha</p>
                <p className="font-medium">{fmtDate(voucher.date, locale)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-content-muted mb-0.5">Descripción</p>
                <p className="font-medium">{voucher.description}</p>
              </div>
              {voucher.source_type && (
                <div className="col-span-2">
                  <p className="text-xs text-content-muted mb-0.5">Origen automático</p>
                  <SourceLink
                    sourceType={voucher.source_type}
                    sourceId={voucher.source_id ?? ''}
                    onClick={() => {
                      if (onNavigateSource && voucher.source_id) {
                        onNavigateSource(voucher.source_type!, voucher.source_id)
                      }
                    }}
                  />
                </div>
              )}
              <div>
                <p className="text-xs text-content-muted mb-0.5">Estado</p>
                <StatusBadge status={voucher.status} catalog={VOUCHER_STATUS} />
              </div>
            </div>

            {/* Líneas */}
            <div>
              <p className="text-xs font-medium text-content-muted uppercase mb-2">Líneas del comprobante</p>
              {voucher.currency && (
                <div className="mb-2 flex items-center gap-2 text-xs text-warning-content bg-warning-surface border border-warning-border rounded px-2 py-1">
                  <span className="font-semibold">{voucher.currency}</span>
                  <span>— Montos en moneda extranjera. CLP equivalente entre paréntesis.</span>
                </div>
              )}
              {(() => {
                const isForeign = !!voucher.currency
                // En moneda extranjera se muestra el monto original y, debajo,
                // su equivalente en CLP: los dos números importan, porque el
                // asiento se contabiliza en CLP y el respaldo está en la otra.
                const fmtForeign = (v: string | null | undefined) => {
                  if (!v) return '—'
                  const n = parseFloat(v)
                  if (isNaN(n) || n === 0) return '—'
                  return n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
                const totalOriginal = (campo: 'debe' | 'haber') => {
                  const tot = voucher.lines.reduce(
                    (s, l) => s + parseFloat((campo === 'debe' ? l.debe_original : l.haber_original) ?? l[campo] ?? '0'),
                    0,
                  )
                  return tot !== 0
                    ? tot.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '—'
                }
                const monto = (l: VoucherLine, campo: 'debe' | 'haber') => {
                  const original = campo === 'debe' ? l.debe_original : l.haber_original
                  if (!isForeign) return fmtCLP(l[campo])
                  return (
                    <span>
                      {fmtForeign(original ?? l[campo])}
                      {l[campo] && parseFloat(l[campo]) !== 0 && (
                        <span className="block text-xs text-content-subtle">{fmtCLP(l[campo])}</span>
                      )}
                    </span>
                  )
                }
                const totalPie = (campo: 'debe' | 'haber') =>
                  isForeign ? (
                    <span>
                      {totalOriginal(campo)}
                      <span className="block text-xs text-content-subtle font-normal">
                        {fmtCLP(campo === 'debe' ? voucher.total_debe : voucher.total_haber)}
                      </span>
                    </span>
                  ) : fmtCLP(campo === 'debe' ? voucher.total_debe : voucher.total_haber)
                return (
                  <DataTable<VoucherLine>
                    columns={[
                      { key: 'account_code', header: 'Cuenta',
                        render: l => (
                          <span className="font-mono text-xs text-content">
                            {l.account_code} — {l.account_name}
                          </span>
                        ) },
                      { key: 'cost_center_code', header: 'CC', width: 80,
                        render: l => (
                          <span className="text-xs text-info-content-soft">{l.cost_center_code || '—'}</span>
                        ) },
                      { key: 'glosa', header: 'Glosa',
                        render: l => <span className="text-content-muted">{l.glosa || '—'}</span> },
                      { key: 'debe', numeric: true,
                        header: `Debe${voucher.currency ? ` (${voucher.currency})` : ''}`,
                        render: l => monto(l, 'debe') },
                      { key: 'haber', numeric: true,
                        header: `Haber${voucher.currency ? ` (${voucher.currency})` : ''}`,
                        render: l => monto(l, 'haber') },
                    ]}
                    rows={voucher.lines}
                    rowKey={(l, i) => l.id ?? i}
                    surface={false}
                    footer={{ debe: totalPie('debe'), haber: totalPie('haber') }}
                    caption="Líneas del comprobante contable"
                  />
                )
              })()}
            </div>

            {/* Adjuntos */}
            <AttachmentsSection voucherId={voucher.id} />
          </div>

        </>
      ) : null}
    </Modal>
  )
}

export function VouchersTab() {
  const locale = useTenantLocale()
  const navigate = useNavigate()
  const _today = todayISO()
  const _firstOfMonth = _today.substring(0, 7) + '-01'
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingVoucher, setEditingVoucher] = useState<AccountingVoucher | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchParams, setSearchParams] = useSearchParams()

  // Auto-abre el detalle cuando llega ?highlight=<voucher_id> (ej. desde intl_invoices)
  useEffect(() => {
    const hl = searchParams.get('highlight')
    if (hl) {
      setDetailId(hl)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const { confirm: confirmAction, modalProps } = useConfirmModal()
  const { can } = usePermission()
  const canCreate = can('accounting.vouchers', 'create')
  const canEdit = can('accounting.vouchers', 'edit')
  const canDelete = can('accounting.vouchers', 'delete')
  const canApprove = can('accounting.vouchers', 'approve')
  // Permiso especial (off por defecto, lo otorga el admin): editar comprobantes ya aprobados.
  const canEditApproved = can('accounting.vouchers', 'edit_approved')

  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const,
      placeholder: 'Buscar descripción...' },
    { key: 'status', type: 'select' as const, label: 'Estado', placement: 'quick' as const,
      options: [
        { value: '', label: 'Todos' },
        { value: 'draft', label: 'Borrador' },
        { value: 'approved', label: 'Aprobado' },
        { value: 'rejected', label: 'Rechazado' },
      ] },
    { key: 'voucher_type', type: 'select' as const, label: 'Tipo', placement: 'quick' as const,
      options: [
        { value: '', label: 'Todos' },
        { value: 'manual', label: 'Manual' },
        { value: 'sale', label: 'Venta' },
        { value: 'purchase', label: 'Compra' },
        { value: 'adjustment', label: 'Ajuste' },
        { value: 'reversal', label: 'Reversión' },
        { value: 'auto', label: 'Automáticos' },
      ] },
    { key: 'date', type: 'date-range' as const, label: 'Fecha', placement: 'quick' as const },
  ], [])

  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1) })

  // Initialize date range to current month
  const dateInitialized = useRef(false)
  useEffect(() => {
    if (!dateInitialized.current) {
      dateInitialized.current = true
      filters.setFilter('date_from', _firstOfMonth)
      filters.setFilter('date_to', _today)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isAutoFilter = filters.values.voucher_type === 'auto'
  const { data, isLoading } = useVouchers({
    ...filters.params,
    voucher_type: isAutoFilter ? undefined : (filters.params.voucher_type as VoucherType || undefined),
    has_source: isAutoFilter ? 'true' : undefined,
    page,
    page_size: pageSize,
  })
  const approve = useApproveVoucher()
  const reverse = useReverseVoucher()
  const deleteVoucher = useDeleteVoucher()
  const reject = useRejectVoucher()
  const bulkApprove = useBulkApproveVouchers()
  const bulkReject = useBulkRejectVouchers()
  // Reversa con fecha elegible (por defecto, la fecha del comprobante original
  // para que la reversa quede en el mismo período contable).
  const [reverseTarget, setReverseTarget] = useState<{ id: string; date: string } | null>(null)

  // Solo los borradores son seleccionables (aprobables/rechazables).
  const draftRows = (data?.vouchers ?? []).filter((v: AccountingVoucherListItem) => v.status === 'draft')
  const allDraftSelected = draftRows.length > 0 && draftRows.every(v => selectedIds.has(v.id))
  const someDraftSelected = draftRows.some(v => selectedIds.has(v.id))

  const clearSelection = () => setSelectedIds(new Set())
  const toggleOne = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const toggleAll = () => setSelectedIds(prev => {
    if (draftRows.every(v => prev.has(v.id))) {
      const next = new Set(prev)
      draftRows.forEach(v => next.delete(v.id))
      return next
    }
    const next = new Set(prev)
    draftRows.forEach(v => next.add(v.id))
    return next
  })

  // Limpiar selección al cambiar de página o de filtros.
  useEffect(() => { clearSelection() }, [page, filters.params]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkApprove = () => {
    const ids = [...selectedIds]
    if (!ids.length) return
    confirmAction({
      message: `¿Aprobar ${ids.length} comprobante(s) seleccionado(s)? Solo se aprobarán los que estén cuadrados.`,
      variant: 'success',
      confirmLabel: 'Aprobar',
      onConfirm: () => {
        setApiError(null); setBulkMessage(null)
        bulkApprove.mutate(ids, {
          onSuccess: (res) => {
            clearSelection()
            const parts = [`${res.approved_count ?? 0} aprobado(s)`]
            if (res.skipped_count) parts.push(`${res.skipped_count} omitido(s) (no cuadran o no son borrador)`)
            setBulkMessage(parts.join(' · '))
          },
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            setApiError(err.response?.data?.error?.message ?? 'Error al aprobar en lote')
          },
        })
      },
    })
  }

  const handleBulkReject = () => {
    const ids = [...selectedIds]
    if (!ids.length) return
    const reason = prompt('Razón del rechazo (opcional):') ?? ''
    confirmAction({
      message: `¿Rechazar ${ids.length} comprobante(s) seleccionado(s)?`,
      variant: 'danger',
      confirmLabel: 'Rechazar',
      onConfirm: () => {
        setApiError(null); setBulkMessage(null)
        bulkReject.mutate({ voucherIds: ids, reason }, {
          onSuccess: (res) => {
            clearSelection()
            const parts = [`${res.rejected_count ?? 0} rechazado(s)`]
            if (res.skipped_count) parts.push(`${res.skipped_count} omitido(s)`)
            setBulkMessage(parts.join(' · '))
          },
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            setApiError(err.response?.data?.error?.message ?? 'Error al rechazar en lote')
          },
        })
      },
    })
  }

  const handleApprove = (id: string) => {
    setApiError(null)
    approve.mutate(id, {
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al aprobar')
      },
    })
  }

  const handleReverse = (id: string, originalDate?: string) => {
    setApiError(null)
    const fallback = (data?.vouchers || []).find(x => x.id === id)?.date
    const today = todayISO()
    setReverseTarget({ id, date: originalDate || fallback || today })
  }

  const confirmReverse = () => {
    if (!reverseTarget) return
    reverse.mutate({ id: reverseTarget.id, date: reverseTarget.date }, {
      onSuccess: () => setReverseTarget(null),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al revertir')
        setReverseTarget(null)
      },
    })
  }

  const handleDelete = (id: string) => {
    confirmAction({
      message: '¿Eliminar este comprobante?',
      variant: 'danger',
      onConfirm: () => {
        deleteVoucher.mutate(id, {
          onError: (e: unknown) => {
            const err = e as { response?: { data?: { error?: { message?: string } } } }
            setApiError(err.response?.data?.error?.message ?? 'Error al eliminar')
          },
        })
      },
    })
  }

  const handleEditApproved = (v: AccountingVoucher) => {
    confirmAction({
      variant: 'danger',
      confirmLabel: 'Editar de todas formas',
      message:
        'Se va a EDITAR un comprobante ya APROBADO. Esto puede descuadrar la contabilidad y ' +
        'afectar balances, EERR, F22/F29 y reportes ya emitidos. El comprobante permanece ' +
        'aprobado y el cambio queda registrado en la bitácora. ¿Continuar?',
      onConfirm: () => { setEditingVoucher(v); setModalOpen(true) },
    })
  }

  const handleReject = (id: string) => {
    const reason = prompt('Razón del rechazo (opcional):') ?? ''
    reject.mutate({ id, reason }, {
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } } }
        setApiError(err.response?.data?.error?.message ?? 'Error al rechazar')
      },
    })
  }

  return (
    <div data-tour="vouchers-page">
      {/* Barra de filtros */}
      <div className="flex items-start gap-2 mb-4">
        <div data-tour="vouchers-filters" className="flex-1 min-w-0">
          <FilterBar config={filterConfig} filters={filters} />
        </div>
        {canCreate && (
          <button
            data-tour="vouchers-new"
            onClick={() => { setEditingVoucher(null); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover shrink-0"
          >
            <Plus size={14} />Nuevo
          </button>
        )}
      </div>

      {apiError && (
        <div className="mb-4 bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">
          {apiError}
        </div>
      )}

      {bulkMessage && (
        <div className="mb-4 bg-success-surface border border-success-border rounded-lg px-3 py-2 text-sm text-success-content flex items-center justify-between">
          <span>{bulkMessage}</span>
          <button onClick={() => setBulkMessage(null)} className="p-1 rounded hover:bg-success-surface-strong"><X size={14} /></button>
        </div>
      )}

      {/* Barra de acciones en lote */}
      {canApprove && selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 flex-wrap bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-content">{selectedIds.size} seleccionado(s)</span>
          <button
            onClick={handleBulkApprove}
            disabled={bulkApprove.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60"
          >
            {bulkApprove.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
            Aprobar ({selectedIds.size})
          </button>
          <button
            onClick={handleBulkReject}
            disabled={bulkReject.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 disabled:opacity-60"
          >
            {bulkReject.isPending ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
            Rechazar ({selectedIds.size})
          </button>
          <button onClick={clearSelection} className="text-sm text-content-muted hover:text-content underline">
            Limpiar
          </button>
        </div>
      )}

      {/*
        🔴 Esto estaba anotado como «no se migra: bloqueo de selección múltiple».
        La premisa era que el `DataTable` no modela selección — y es cierta—,
        pero la conclusión no se seguía: **la casilla es una columna más**. Su
        estado sigue viviendo en esta pantalla (`selectedIds`, `toggleOne`,
        `toggleAll`), el componente solo la pinta, y el resaltado de la fila
        elegida lo da `rowClassName`. No se ofrece «selección a medias»: es
        exactamente la misma que había.

        Lo que sí costaba no migrarla: los montos salían en la tipografía del
        texto en vez de la tabular del sistema, porque `numeric` es del
        componente. Reportado mirando la pantalla, no los tests.
      */}
      {isLoading ? (
        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider shadow-card flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando...
        </div>
      ) : (
        <DataTable
          data-tour="vouchers-table"
          columns={[
            ...(canApprove ? [{
              key: 'sel',
              width: 40,
              header: (
                <input
                  type="checkbox"
                  aria-label="Seleccionar todos los borradores"
                  checked={allDraftSelected}
                  ref={el => { if (el) el.indeterminate = someDraftSelected && !allDraftSelected }}
                  onChange={toggleAll}
                  disabled={draftRows.length === 0}
                  className="cursor-pointer accent-primary disabled:opacity-40"
                />
              ),
              // ⚠️ La casilla solo existe en los borradores: un comprobante
              // aprobado no se vuelve a aprobar en lote.
              render: (v: AccountingVoucherListItem) => (
                <div className="text-center" onClick={e => e.stopPropagation()}>
                  {v.status === 'draft' && (
                    <input
                      type="checkbox"
                      aria-label="Seleccionar comprobante"
                      checked={selectedIds.has(v.id)}
                      onChange={() => toggleOne(v.id)}
                      className="cursor-pointer accent-primary"
                    />
                  )}
                </div>
              ),
            } as DataTableColumn<AccountingVoucherListItem>] : []),
            { key: 'number', header: 'Número',
              render: v => (
                <span className="font-mono">
                  {v.number || <span className="text-content-subtle italic text-xs">Borrador</span>}
                </span>
              ) },
            { key: 'date', header: 'Fecha', sortValue: v => v.date,
              render: v => <span className="text-content-muted">{fmtDate(v.date, locale)}</span> },
            { key: 'voucher_type', header: 'Tipo',
              render: v => <span className="text-content-muted">{TYPE_LABELS[v.voucher_type]}</span> },
            { key: 'description', header: 'Descripción',
              render: v => (
                <div className="max-w-[200px] truncate">
                  {v.description}
                  {v.source_type && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-info-surface text-info-content-soft rounded border border-info-border"
                      title={`Ver ${SOURCE_TYPE_CONFIG[v.source_type]?.label ?? v.source_type}`}>
                      <Zap size={9} />Auto
                    </span>
                  )}
                </div>
              ) },
            // `numeric` es lo que faltaba: monoespaciado, tabular y a la derecha,
            // igual que los totales del listado de documentos.
            { key: 'total_debe', header: 'Debe', numeric: true,
              render: v => <span className="font-medium">{fmtCLP(v.total_debe)}</span> },
            { key: 'total_haber', header: 'Haber', numeric: true,
              render: v => <span className="font-medium">{fmtCLP(v.total_haber)}</span> },
            { key: 'currency', header: 'Moneda',
              render: v => (
                <div className="text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium ${v.currency ? 'bg-warning-surface text-warning-content border border-warning-border' : 'text-content-subtle'}`}>
                    {v.currency || 'CLP'}
                  </span>
                </div>
              ) },
            { key: 'status', header: 'Estado',
              render: v => <StatusBadge status={v.status} catalog={VOUCHER_STATUS} /> },
            { key: 'acciones', header: 'Acciones',
              render: v => (
                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  {v.status === 'draft' && (
                    <>
                      {canEdit && (
                        <button
                          onClick={() => { setEditingVoucher(v as unknown as AccountingVoucher); setModalOpen(true) }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft"
                        >
                          <Pencil size={10} />Editar
                        </button>
                      )}
                      {canApprove && (
                        <button
                          onClick={() => handleApprove(v.id)}
                          disabled={approve.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60"
                        >
                          <CheckCircle size={10} />Aprobar
                        </button>
                      )}
                      {canApprove && (
                        <button
                          onClick={() => handleReject(v.id)}
                          disabled={reject.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content-soft border border-warning-border rounded-lg hover:bg-warning-surface disabled:opacity-60"
                        >
                          <XCircle size={10} />Rechazar
                        </button>
                      )}
                    </>
                  )}
                  <KebabMenu items={[
                    { label: 'Editar (aprobado)', icon: <AlertTriangle size={14} />, onClick: () => handleEditApproved(v as unknown as AccountingVoucher), hidden: v.status !== 'approved' || !canEditApproved, className: 'text-danger-content-soft hover:bg-danger-surface' },
                    { label: 'Eliminar', icon: <Trash2 size={14} />, onClick: () => handleDelete(v.id), hidden: v.status !== 'draft' || !canDelete, className: 'text-danger-content-soft hover:bg-danger-surface', disabled: deleteVoucher.isPending },
                    { label: 'Revertir', icon: <RotateCcw size={14} />, onClick: () => handleReverse(v.id, v.date), hidden: v.status !== 'approved' || !canApprove, className: 'text-warning-content-soft hover:bg-warning-surface', disabled: reverse.isPending },
                  ]} />
                </div>
              ) },
          ]}
          rows={data?.vouchers ?? []}
          rowKey={v => v.id}
          onRowClick={v => setDetailId(v.id)}
          rowClassName={v => (selectedIds.has(v.id) ? 'bg-primary-50/60' : undefined)}
          empty={
            <div className="text-center py-16">
              <FileText size={32} className="mx-auto text-content-subtle mb-3" />
              <p className="text-content-muted text-sm font-medium">Sin comprobantes</p>
              {canCreate && (
                <button
                  onClick={() => { setEditingVoucher(null); setModalOpen(true) }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
                >
                  <Plus size={14} />Crear primer comprobante
                </button>
              )}
            </div>
          }
          minWidth={1100}
        />
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
        <PageSizeSelector
          value={pageSize}
          onChange={n => { setPageSize(n); setPage(1) }}
          storageKey="vouchers-page-size"
        />
        {data && data.pages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.count}
            onPageChange={setPage}
            locale={locale}
          />
        )}
      </div>

      {modalOpen && (
        <VoucherForm
          voucher={editingVoucher}
          onClose={() => { setModalOpen(false); setEditingVoucher(null) }}
        />
      )}

      {detailId && (
        <VoucherDetailModal
          voucherId={detailId}
          onClose={() => setDetailId(null)}
          onEdit={v => { setEditingVoucher(v); setModalOpen(true) }}
          onEditApproved={v => handleEditApproved(v)}
          onApprove={handleApprove}
          onReject={handleReject}
          onReverse={id => handleReverse(id)}
          onDelete={handleDelete}
          onNavigateSource={(sourceType, sourceId) => {
            setDetailId(null)
            const cfg = SOURCE_TYPE_CONFIG[sourceType]
            if (cfg) navigate(`${cfg.route}?highlight=${sourceId}`)
          }}
        />
      )}

      {reverseTarget && (
        <Modal open title="Revertir comprobante" onClose={() => setReverseTarget(null)} size="sm"
          actions={
            <>
              <button type="button" onClick={() => setReverseTarget(null)} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
              <button type="button" onClick={confirmReverse} disabled={reverse.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-warning-fill hover:bg-warning-fill-hover text-white disabled:opacity-50">
                {reverse.isPending ? 'Revirtiendo…' : 'Revertir'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-content-muted">
              Se generará un comprobante de reversión que anula este movimiento. Elige la fecha de la reversa
              (por defecto, la del comprobante original para que quede en el mismo período).
            </p>
            <div>
              <label htmlFor="voucherstab-fecha-de-la-reversa" className="block text-xs font-semibold text-content-muted mb-1">Fecha de la reversa</label>
              <DatePicker id="voucherstab-fecha-de-la-reversa" value={reverseTarget.date} onChange={(d: string) => setReverseTarget(t => t ? { ...t, date: d } : t)} />
            </div>
          </div>
        </Modal>
      )}

      <ConfirmModal {...modalProps} />
    </div>
  )
}
